import { CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE } from 'constants/search'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import { deleteSearchParams, setSearchParamValue, toggleSearchParamValue } from 'lib/url/searchUrlUtils'
import type { EnabledFilters } from 'components/Search/type'

const RANKED_POPULAR_FILTERS_MIN_COUNT = 3
const RANKED_POPULAR_FILTERS_MAX_COUNT = 5

type RankedPopularFilterGroup =
  | 'inclusions'
  | 'holidayTypes'
  | 'propertyTypes'
  | 'customerRatings'
  | 'roomAmenities'
  | 'propertyFacilities'

interface GroupConfig {
  enabledFilterKey: keyof EnabledFilters
  searchParam: string
}

const GROUP_CONFIG: Record<RankedPopularFilterGroup, GroupConfig> = {
  inclusions: { enabledFilterKey: 'inclusions', searchParam: 'inclusions' },
  holidayTypes: { enabledFilterKey: 'holidayTypes', searchParam: 'holidayTypes' },
  propertyTypes: { enabledFilterKey: 'propertyTypes', searchParam: 'propertyTypes' },
  customerRatings: { enabledFilterKey: 'customerRating', searchParam: 'customerRatingGte' },
  roomAmenities: { enabledFilterKey: 'roomAmenities', searchParam: 'roomAmenities' },
  propertyFacilities: { enabledFilterKey: 'propertyFacilities', searchParam: 'propertyFacilities' },
}

export interface RankedPopularFilterItem {
  group: RankedPopularFilterGroup
  slug: string
  value: string | number
  label: string
  count: number
  searchParam: string
}

function isRankedPopularFilterGroup(key: string): key is RankedPopularFilterGroup {
  return key in GROUP_CONFIG
}

function getRatingValue(slug: string): number | undefined {
  const entry = Object.entries(CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE).find(([, ratingSlug]) => ratingSlug === slug)
  return entry ? Number(entry[0]) : undefined
}

function getFilterValue(group: RankedPopularFilterGroup, slug: string): string | number | undefined {
  return group === 'customerRatings' ? getRatingValue(slug) : slug
}

export function isRankedPopularFilterChecked(item: RankedPopularFilterItem, filters: App.OfferListFilters): boolean {
  switch (item.group) {
    case 'customerRatings':
      return filters.customerRatingGte === item.value
    case 'inclusions':
      return !!filters.inclusions?.includes(item.slug)
    case 'holidayTypes':
      return !!filters.holidayTypes?.includes(item.slug)
    case 'propertyTypes':
      return !!filters.propertyTypes?.includes(item.slug)
    case 'roomAmenities':
      return !!filters.roomAmenities?.includes(item.slug)
    case 'propertyFacilities':
      return !!filters.propertyFacilities?.includes(item.slug)
  }
}

export function getRankedPopularFilterSearch(
  search: string,
  item: RankedPopularFilterItem,
  filters: App.OfferListFilters,
): string {
  if (item.group !== 'customerRatings') {
    return toggleSearchParamValue(search, item.searchParam, item.value)
  }

  return isRankedPopularFilterChecked(item, filters) ?
      deleteSearchParams(search, item.searchParam) :
      setSearchParamValue(search, item.searchParam, item.value)
}

export function getRankedPopularFilterItems(
  popularFilters: Array<App.RankedPopularFilter> | undefined,
  availableFilters: App.OfferListAvailableFilters | undefined,
  enabledFilters: EnabledFilters,
): Array<RankedPopularFilterItem> {
  if (!popularFilters || !availableFilters) return EmptyArray

  const seenValues = new Set<string | number>()
  const items = [...popularFilters]
    .sort((a, b) => a.rank - b.rank)
    .flatMap((entry): Array<RankedPopularFilterItem> => {
      if (!isRankedPopularFilterGroup(entry.key)) return EmptyArray

      const { enabledFilterKey, searchParam } = GROUP_CONFIG[entry.key]
      if (!enabledFilters[enabledFilterKey]) return EmptyArray

      const option = availableFilters[entry.key]?.find(item => item.slug === entry.slug)
      const value = getFilterValue(entry.key, entry.slug)
      if (!option?.count || value === undefined || seenValues.has(value)) return EmptyArray
      seenValues.add(value)

      return [{
        group: entry.key,
        slug: entry.slug,
        value,
        label: option.label,
        count: option.count,
        searchParam,
      }]
    })

  const visibleItems = take(items, RANKED_POPULAR_FILTERS_MAX_COUNT)
  return visibleItems.length >= RANKED_POPULAR_FILTERS_MIN_COUNT ? visibleItems : EmptyArray
}
